import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "../lib/api";
import { Plus, Trash2, Save, X, MessagesSquare } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["operations", "attendance", "reception", "housekeeping", "maintenance", "emergency"];

function extractPlaceholders(text) {
    const out = new Set();
    const re = /\{([a-zA-Z0-9_]+)\}/g;
    let m;
    while ((m = re.exec(text || ""))) out.add(m[1]);
    return Array.from(out);
}

export default function PresetManagerPage() {
    const [presets, setPresets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawer, setDrawer] = useState(null); // null | "new" | preset obj
    const [filter, setFilter] = useState("ALL");

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/chat/presets");
            setPresets(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        if (filter === "ALL") return presets;
        return presets.filter((p) => (p.category || "operations") === filter);
    }, [presets, filter]);

    async function remove(p) {
        if (!window.confirm(`Delete preset "${p.key}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/chat/presets/${p.id}`);
            toast.success("Preset deleted");
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">HO Admin</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Chat Preset Library</h1>
                    <p className="text-sm text-gray-500 mt-1">Shared trilingual presets — receivers see the message in their own language.</p>
                </div>
                <button onClick={() => setDrawer("new")} className="inline-flex items-center gap-1.5 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="new-preset-btn">
                    <Plus className="w-4 h-4" /> New preset
                </button>
            </div>

            <div className="mt-5 flex flex-wrap gap-2" data-testid="preset-filter-row">
                {["ALL", ...CATEGORIES].map((c) => (
                    <button key={c} onClick={() => setFilter(c)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === c ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-gray-600 border-gray-200 hover:border-[#1E3355]"}`}
                        data-testid={`preset-filter-${c}`}>
                        {c === "ALL" ? "All" : c}
                    </button>
                ))}
            </div>

            <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="presets-table">
                        <thead className="bg-[#F4F6F9]">
                            <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                <th className="px-4 py-3">Key</th>
                                <th className="px-4 py-3">Category</th>
                                <th className="px-4 py-3">English</th>
                                <th className="px-4 py-3">हिन्दी</th>
                                <th className="px-4 py-3">বাংলা</th>
                                <th className="px-4 py-3">Placeholders</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr><td colSpan="7" className="p-6 text-center text-sm text-gray-500">Loading…</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan="7" className="p-6 text-center text-sm text-gray-500">No presets in this category.</td></tr>
                            ) : filtered.map((p) => (
                                <tr key={p.id} className="hover:bg-[#FAFBFC] align-top" data-testid={`preset-row-${p.key}`}>
                                    <td className="px-4 py-3 font-mono text-xs text-[#1E3355] whitespace-nowrap">{p.key}</td>
                                    <td className="px-4 py-3">
                                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#F4F6F9] text-gray-600 border border-gray-200 uppercase tracking-wider">{p.category || "—"}</span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-800 max-w-xs">{p.en}</td>
                                    <td className="px-4 py-3 text-gray-800 max-w-xs">{p.hi}</td>
                                    <td className="px-4 py-3 text-gray-800 max-w-xs">{p.bn}</td>
                                    <td className="px-4 py-3">
                                        {(p.placeholders || []).length ? (
                                            <div className="flex flex-wrap gap-1">
                                                {(p.placeholders || []).map((ph) => (
                                                    <span key={ph} className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono bg-amber-50 text-amber-700 border border-amber-200">{`{${ph}}`}</span>
                                                ))}
                                            </div>
                                        ) : <span className="text-gray-400 text-xs">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        <button onClick={() => setDrawer(p)} className="px-2 py-1 text-xs font-medium text-[#1E3355] hover:bg-gray-100 rounded" data-testid={`edit-preset-${p.key}`}>Edit</button>
                                        <button onClick={() => remove(p)} className="p-1.5 rounded hover:bg-red-50 text-[#E02B2B] ml-1" title="Delete" data-testid={`delete-preset-${p.key}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {drawer && (
                <PresetDrawer
                    key={drawer === "new" ? "new" : drawer.id}
                    preset={drawer === "new" ? null : drawer}
                    onClose={() => setDrawer(null)}
                    onSaved={() => { setDrawer(null); load(); }}
                />
            )}
        </div>
    );
}

function PresetDrawer({ preset, onClose, onSaved }) {
    const isNew = !preset;
    const [form, setForm] = useState({
        key: preset?.key || "",
        category: preset?.category || "operations",
        en: preset?.en || "",
        hi: preset?.hi || "",
        bn: preset?.bn || "",
    });
    const [saving, setSaving] = useState(false);

    const placeholders = useMemo(() => {
        const merged = new Set([
            ...extractPlaceholders(form.en),
            ...extractPlaceholders(form.hi),
            ...extractPlaceholders(form.bn),
        ]);
        return Array.from(merged);
    }, [form.en, form.hi, form.bn]);

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form, placeholders };
            if (isNew) {
                await api.post("/chat/presets", payload);
                toast.success(`Created preset "${form.key}"`);
            } else {
                await api.patch(`/chat/presets/${preset.id}`, payload);
                toast.success("Preset updated");
            }
            onSaved();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end" data-testid="preset-drawer">
            <form onSubmit={submit} className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl">
                <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{isNew ? "New preset" : "Edit preset"}</div>
                        <div className="font-display text-lg font-bold text-[#1E3355] flex items-center gap-2">
                            <MessagesSquare className="w-5 h-5 text-[#E02B2B]" />
                            {form.key || "—"}
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-gray-100" data-testid="preset-drawer-close"><X className="w-4 h-4 text-gray-600" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <Field label="Key (snake_case, unique)">
                        <input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} required disabled={!isNew} className={`${fieldCls} disabled:bg-gray-50 font-mono text-xs`} data-testid="preset-key" />
                    </Field>
                    <Field label="Category">
                        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={fieldCls} data-testid="preset-category">
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </Field>
                    <Field label="English" hint="Use {name} etc. for fill-in placeholders.">
                        <textarea value={form.en} onChange={(e) => setForm({ ...form, en: e.target.value })} rows={2} required className={fieldCls} data-testid="preset-en" />
                    </Field>
                    <Field label="हिन्दी (Hindi)">
                        <textarea value={form.hi} onChange={(e) => setForm({ ...form, hi: e.target.value })} rows={2} required className={fieldCls} data-testid="preset-hi" />
                    </Field>
                    <Field label="বাংলা (Bengali)">
                        <textarea value={form.bn} onChange={(e) => setForm({ ...form, bn: e.target.value })} rows={2} required className={fieldCls} data-testid="preset-bn" />
                    </Field>
                    <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Detected placeholders</div>
                        {placeholders.length ? (
                            <div className="flex flex-wrap gap-1" data-testid="preset-placeholders">
                                {placeholders.map((p) => (
                                    <span key={p} className="inline-flex px-2 py-0.5 rounded text-[11px] font-mono bg-amber-50 text-amber-700 border border-amber-200">{`{${p}}`}</span>
                                ))}
                            </div>
                        ) : <div className="text-xs text-gray-400">None</div>}
                    </div>
                </div>
                <div className="p-5 border-t border-gray-100 flex gap-2 sticky bottom-0 bg-white">
                    <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-md border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50" data-testid="preset-cancel">Cancel</button>
                    <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-md bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-1.5" data-testid="preset-save">
                        <Save className="w-4 h-4" /> {saving ? "Saving…" : isNew ? "Create preset" : "Save"}
                    </button>
                </div>
            </form>
        </div>
    );
}

const fieldCls = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355]";
function Field({ label, hint, children }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            {children}
            {hint && <p className="mt-1 text-[11px] text-gray-500">{hint}</p>}
        </div>
    );
}
